// Shared browser-mic <-> ElevenLabs audio codec helpers, used by any
// component that streams raw ulaw/pcm16 audio over a WebSocket (ChatComponent,
// TestAgentComponent).

export const VOICE_SAMPLE_RATE = 8000;
export const VOICE_BUFFER_SIZE = 4096;
export const USER_AUDIO_FORMAT = 'ulaw'; // Change to 'pcm16' if your backend/ElevenLabs agent expects PCM16 input.
export const AGENT_AUDIO_FORMAT = 'ulaw'; // Change to 'pcm16' if your ElevenLabs agent returns raw PCM16.

export const base64ToUint8Array = (base64) => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }

    return bytes;
};

export const muLawToLinearSample = (muLawByte) => {
    const BIAS = 0x84;
    const byte = ~muLawByte & 0xff;
    const sign = byte & 0x80;
    const exponent = (byte >> 4) & 0x07;
    const mantissa = byte & 0x0f;
    let sample = ((mantissa << 3) + BIAS) << exponent;
    sample -= BIAS;

    return sign ? -sample : sample;
};

export const decodeAgentAudio = (base64Audio, format = AGENT_AUDIO_FORMAT) => {
    const bytes = base64ToUint8Array(base64Audio);

    if (format === 'pcm16') {
        const sampleCount = Math.floor(bytes.length / 2);
        const samples = new Float32Array(sampleCount);
        const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

        for (let i = 0; i < sampleCount; i++) {
            samples[i] = view.getInt16(i * 2, true) / 32768;
        }

        return samples;
    }

    const samples = new Float32Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) {
        samples[i] = muLawToLinearSample(bytes[i]) / 32768;
    }

    return samples;
};

export const downsampleBuffer = (buffer, inputSampleRate, outputSampleRate) => {
    if (outputSampleRate === inputSampleRate) {
        return buffer;
    }

    const sampleRateRatio = inputSampleRate / outputSampleRate;
    const newLength = Math.round(buffer.length / sampleRateRatio);
    const result = new Float32Array(newLength);
    let offsetResult = 0;
    let offsetBuffer = 0;

    while (offsetResult < result.length) {
        const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
        let accum = 0;
        let count = 0;

        for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
            accum += buffer[i];
            count++;
        }

        result[offsetResult] = count ? accum / count : 0;
        offsetResult++;
        offsetBuffer = nextOffsetBuffer;
    }

    return result;
};

export const floatTo16BitPcm = (floatSamples) => {
    const buffer = new ArrayBuffer(floatSamples.length * 2);
    const view = new DataView(buffer);

    for (let i = 0; i < floatSamples.length; i++) {
        const sample = Math.max(-1, Math.min(1, floatSamples[i]));
        view.setInt16(i * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
    }

    return buffer;
};

export const linearSampleToMuLaw = (sample) => {
    const BIAS = 0x84;
    const CLIP = 32635;
    let pcm = Math.max(-1, Math.min(1, sample));
    pcm = pcm < 0 ? pcm * 0x8000 : pcm * 0x7fff;

    let sign = 0;
    if (pcm < 0) {
        sign = 0x80;
        pcm = -pcm;
    }

    pcm = Math.min(CLIP, pcm) + BIAS;

    let exponent = 7;
    for (let mask = 0x4000; (pcm & mask) === 0 && exponent > 0; mask >>= 1) {
        exponent--;
    }

    const mantissa = (pcm >> (exponent + 3)) & 0x0f;
    return ~(sign | (exponent << 4) | mantissa) & 0xff;
};

export const floatToMuLaw = (floatSamples) => {
    const bytes = new Uint8Array(floatSamples.length);

    for (let i = 0; i < floatSamples.length; i++) {
        bytes[i] = linearSampleToMuLaw(floatSamples[i]);
    }

    return bytes.buffer;
};

export const encodeUserAudio = (floatSamples, format = USER_AUDIO_FORMAT) => {
    return format === 'pcm16'
        ? floatTo16BitPcm(floatSamples)
        : floatToMuLaw(floatSamples);
};
